/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Group, Paper } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { CalendarMonth } from "@mui/icons-material";
import dayjs from "dayjs";
import React, { useEffect, useState, useRef } from "react";
import "dayjs/locale/ru";

const DatesPicker = ({
    onChange,
    initialStartDate = null,
    initialEndDate = null,
    startLabel = "Дата начала",
    endLabel = "Дата окончания",
    disablePast = false,
    disableFuture = false,
    required = false,
    minDate,
    maxDate,
    sx = {},
}) => {
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);

    const [startDateError, setStartDateError] = useState("");
    const [endDateError, setEndDateError] = useState("");

    const prevStartDateRef = useRef(initialStartDate);
    const prevEndDateRef = useRef(initialEndDate);
    const isInitialRender = useRef(true);

    const today = new Date();

    const getMinDate = () => {
        if (disablePast) {
            return today;
        }
        return minDate || undefined;
    };

    const getMaxDate = () => {
        if (disableFuture) {
            return today;
        }
        return maxDate || undefined;
    };

    const areDatesEqual = (date1, date2) => {
        if (date1 === null && date2 === null) return true;
        if (date1 === null || date2 === null) return false;
        return dayjs(date1).isSame(dayjs(date2), "day");
    };

    useEffect(() => {
        setStartDateError("");
        setEndDateError("");

        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }

        if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
            setEndDateError("End date must be after start date");
            return;
        }

        const startDateChanged = !areDatesEqual(
            startDate,
            prevStartDateRef.current
        );
        const endDateChanged = !areDatesEqual(endDate, prevEndDateRef.current);

        if ((startDateChanged || endDateChanged) && onChange) {
            onChange(startDate, endDate);
            prevStartDateRef.current = startDate;
            prevEndDateRef.current = endDate;
        }
    }, [startDate, endDate]);

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const excludeStartDate = (date) => {
        const minAllowed = getMinDate();
        const maxAllowed = getMaxDate();

        if (minAllowed && dayjs(date).isBefore(dayjs(minAllowed), "day")) {
            return true;
        }

        if (maxAllowed && dayjs(date).isAfter(dayjs(maxAllowed), "day")) {
            return true;
        }

        return false;
    };

    const excludeEndDate = (date) => {
        if (startDate && dayjs(date).isBefore(dayjs(startDate), "day")) {
            return true;
        }

        const minAllowed = getMinDate();
        const maxAllowed = getMaxDate();

        if (minAllowed && dayjs(date).isBefore(dayjs(minAllowed), "day")) {
            return true;
        }

        if (maxAllowed && dayjs(date).isAfter(dayjs(maxAllowed), "day")) {
            return true;
        }

        return false;
    };

    return (
        <Paper p="md" sx={{ ...sx }}>
            <Group position="apart" grow spacing="sm">
                <Box>
                    <DatePickerInput
                        label={startLabel}
                        placeholder="Укажите дату"
                        value={startDate}
                        onChange={handleStartDateChange}
                        error={startDateError}
                        withAsterisk={required}
                        clearable
                        popoverProps={{ withinPortal: true }}
                        excludeDate={excludeStartDate}
                        icon={<CalendarMonth size={14} />}
                        locale="ru"
                    />
                </Box>

                <Box>
                    <DatePickerInput
                        label={endLabel}
                        placeholder="Укажите дату"
                        value={endDate}
                        onChange={handleEndDateChange}
                        error={endDateError}
                        withAsterisk={required}
                        clearable
                        popoverProps={{ withinPortal: true }}
                        excludeDate={excludeEndDate}
                        icon={<CalendarMonth size={14} />}
                        locale="ru"
                    />
                </Box>
            </Group>
        </Paper>
    );
};

export default DatesPicker;
